/**
 * SSOT-ENTITLEMENT-PRESENTATION-01 — frontend quota presentation contract.
 *
 * The backend (`EntitlementAuthority.get_quota_presentation`, exposed as
 * `entitlement` on GET /api/clinic/quota-summary) is the SINGLE authority for
 * what a clinic is entitled to and how much it has used. This module performs
 * FORMATTING ONLY:
 *
 *   state = 'unlimited'  → ∞            (never a percentage, never a warning)
 *   state = 'finite'     → used / limit (0 / 0 is a real, configured deny)
 *   configured = false   → —            (not configured: never rendered as 0,
 *                                        never produces a limit warning)
 *
 * It resolves NO alias chains, invents NO fallback limit and performs NO
 * business arithmetic beyond the displayed percentage.
 */

export const UNLIMITED_GLYPH = '\u221E';
export const UNDEFINED_GLYPH = '\u2014';

export const getResource = (entitlement, key) =>
  (entitlement && entitlement.resources && entitlement.resources[key]) || null;

export const isResolved = (resource) =>
  Boolean(resource) && resource.configured === true && resource.state !== 'undefined';

export const formatLimit = (resource) => {
  if (!isResolved(resource)) return UNDEFINED_GLYPH;
  if (resource.unlimited) return UNLIMITED_GLYPH;
  return String(resource.limit);
};

export const formatCapacity = (resource) => {
  if (!resource) return UNDEFINED_GLYPH;
  return `${resource.used ?? 0} / ${formatLimit(resource)}`;
};

/** Percentage for a finite cap only. `null` when not applicable. */
export const quotaPercentage = (resource) => {
  if (!isResolved(resource) || resource.unlimited) return null;
  const limit = Number(resource.limit);
  if (limit === 0) return 100;
  return Math.min(100, Math.round(((resource.used ?? 0) / limit) * 100));
};

/** 'reached' | 'approaching' | null — only ever derived from a finite cap. */
export const warningLevel = (resource) => {
  const pct = quotaPercentage(resource);
  if (pct === null) return null;
  if (pct >= 100) return 'reached';
  if (pct >= 80) return 'approaching';
  return null;
};
